import { BellOutlined, MenuOutlined, QuestionCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { type Language } from "@ap/i18n";
import { Term, useAuth } from "@ap/ui-kit";
import { Badge, Popover, Tag } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { routeOf } from "../routes";
import { ProductMark } from "./Rail";
import { useShellUi } from "./shellUi";

function isMac(): boolean {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
}

/** "Needs you" bell: total count badge + a popover listing each source with its count. */
export function NeedsBell() {
  const { t } = useTranslation("home");
  const { needs, needsTotal } = useShellUi();
  const [open, setOpen] = useState(false);
  if (needs.length === 0) return null;
  const label = needsTotal > 0 ? t("needs.bellCount", { count: needsTotal }) : t("needs.bell");
  const content = (
    <div className="ap-bell-pop" data-testid="bell-pop">
      <div className="ap-bell-title">{t("needs.bellTitle")}</div>
      {needs.every((n) => n.count !== null && n.count <= 0) ? <p className="ap-muted">{t("needs.none")}</p> : null}
      <ul className="ap-bell-list">
        {needs
          .filter((n) => n.count === null || n.count !== 0)
          .map(({ source, count }) => (
            <li key={source.key}>
              <Link to={source.path} onClick={() => setOpen(false)} className="ap-bell-item">
                <span>{t(`needs.${source.key}.title`)}</span>
                <span className="ap-count" data-testid={`bell-count-${source.key}`}>
                  {count === null ? "…" : count < 0 ? "!" : count}
                </span>
              </Link>
            </li>
          ))}
      </ul>
    </div>
  );
  return (
    <Popover content={content} trigger="click" open={open} onOpenChange={setOpen} placement="bottomRight">
      <button type="button" className="ap-icon-btn" aria-label={label} aria-expanded={open} aria-haspopup="dialog" data-testid="needs-bell" data-tour="bell">
        <Badge count={needsTotal} size="small" overflowCount={99}>
          <BellOutlined aria-hidden className="ap-icon-btn-icon" />
        </Badge>
      </button>
    </Popover>
  );
}

export interface TopBarProps {
  lang: Language;
  onLanguage: (l: Language) => void;
  /** Small screens: show the menu button that opens the navigation drawer. */
  compact: boolean;
  onMenu: () => void;
}

export function TopBar({ lang, onLanguage, compact, onMenu }: TopBarProps) {
  const { t } = useTranslation("shell");
  const { user, mode } = useAuth();
  const { openPalette, openHelp } = useShellUi();
  const loc = useLocation();
  const route = routeOf(loc.pathname);
  const other: Language = lang === "ar" ? "en" : "ar";
  return (
    <header className="ap-topbar">
      <div className="ap-topbar-start">
        {compact ? (
          <>
            <button type="button" className="ap-icon-btn" aria-label={t("rail.openMenu")} onClick={onMenu} data-testid="menu-button">
              <MenuOutlined aria-hidden className="ap-icon-btn-icon" />
            </button>
            <Link to="/home" aria-label={t("rail.home")} className="ap-topbar-mark">
              <ProductMark size={26} />
            </Link>
          </>
        ) : null}
        <nav aria-label={t("breadcrumb")} className="ap-crumbs">
          <ol>
            {route && route.group !== "home" ? (
              <li className="ap-crumb-group">{t(`group.${route.group}`)}</li>
            ) : null}
            <li aria-current="page" className="ap-crumb-page">
              {route ? t(route.label) : t("product")}
            </li>
          </ol>
        </nav>
        {user?.tenant && !compact ? (
          <span className="ap-chip" data-testid="tenant-chip">
            <Term id="tenant">{t("tenantChip")}</Term>
            <bdi className="ap-chip-value">{user.tenant === "_platform" ? t("platformTeam") : user.tenant}</bdi>
          </span>
        ) : null}
        {mode === "test" ? <Tag color="red">{t("testMode")}</Tag> : null}
      </div>
      <div className="ap-topbar-end">
        <button type="button" className="ap-search" onClick={openPalette} aria-haspopup="dialog" aria-keyshortcuts="Control+K Meta+K" data-tour="palette" data-testid="palette-open">
          <SearchOutlined aria-hidden />
          <span className={compact ? "ap-visually-hidden" : "ap-search-text"}>{t("searchJump")}</span>
          {!compact ? (
            <kbd className="ap-kbd" aria-hidden>
              {isMac() ? "⌘K" : "Ctrl K"}
            </kbd>
          ) : null}
        </button>
        <NeedsBell />
        <button type="button" className="ap-icon-btn" aria-label={t("help")} onClick={openHelp} data-tour="help" data-testid="help-open">
          <QuestionCircleOutlined aria-hidden className="ap-icon-btn-icon" />
        </button>
        <button type="button" className="ap-lang" onClick={() => onLanguage(other)} lang={other} aria-label={t("switchLanguage")} title={other === "ar" ? "العربية" : "English"}>
          {t("langShort")}
        </button>
      </div>
    </header>
  );
}
